import { WalletAdapterNetwork } from '@solana/wallet-adapter-base'
import { ConnectionProvider, WalletProvider } from '@solana/wallet-adapter-react'
import {
    CoinbaseWalletAdapter,
    FractalWalletAdapter,
    LedgerWalletAdapter,
    PhantomWalletAdapter,
    SolflareWalletAdapter,
    TrustWalletAdapter,
} from '@solana/wallet-adapter-wallets'
import { clusterApiUrl } from '@solana/web3.js'
import { FC, ReactNode, useMemo } from 'react'

export const WalletContext: FC<{ children: ReactNode }> = ({ children }) => {
    // The network can be set to 'devnet', 'testnet', or 'mainnet-beta'.
    const network = WalletAdapterNetwork.Mainnet

    // `clusterApiUrl` is Solana's public endpoint, which is heavily rate-limited and not meant for
    // production traffic. Nothing under this provider actually reads chain state through it: the
    // deposit and lootbox flows each build their own `Connection` from the backend's
    // `SOLANA_CLIENT_RPC_URL`, and account linking only signs. Anything added here that calls
    // `useConnection()` needs a real endpoint threaded in rather than this default.
    const endpoint = useMemo(() => clusterApiUrl(network), [network])

    // @solana/wallet-adapter-wallets includes all the adapters but supports tree shaking and lazy loading --
    // Only the wallets you configure here will be compiled into your application, and only the dependencies
    // of wallets that your users connect to will be loaded.
    //
    // **This list is not the list of wallets players see.** Anything implementing the Wallet
    // Standard — Backpack, Glow, Nightly, OKX, Magic Eden, and most wallets shipped since 2023 —
    // registers itself and is surfaced automatically by `wallet-standard-wallet-adapter-react`,
    // which `@solana/wallet-adapter-react` pulls in. Mobile Wallet Adapter is added on Android the
    // same way. The visible list therefore differs per browser and per visitor; that is expected,
    // and `useAvailableWallets` is where it gets filtered.
    //
    // Deliberately not registered:
    // - WalletConnect. Not drop-in: the adapter requires a WalletConnect Cloud `projectId`, so it
    //   needs an account and config rather than a line here.
    // - Torus. Social login is a different sign-in model, and it reports `Loadable`, so it would
    //   appear for every user whether or not they want that route.
    // - MetaMask. There is no Solana MetaMask adapter. Modern MetaMask registers through the
    //   Wallet Standard once the player has created a Solana account in it, which is why it shows
    //   up for some players and not others — nothing here can change that.
    // - Brave Wallet. Same: it self-registers, but only when Brave's Solana setting is "Brave
    //   Wallet" rather than delegating to extensions.
    const wallets = useMemo(
        () => [
            new PhantomWalletAdapter(),
            new SolflareWalletAdapter({ network }),
            new LedgerWalletAdapter(),
            new CoinbaseWalletAdapter(),
            new TrustWalletAdapter(),
            new FractalWalletAdapter(),
        ],
        [network]
    )

    // `autoConnect` is off on purpose. Reconnecting without a click makes the wallet popup appear
    // on page load, and on Brave — where the built-in wallet and an extension can both claim
    // `window.solana` — it can bind to the wrong provider before the player has chosen one.
    return (
        <ConnectionProvider endpoint={endpoint}>
            <WalletProvider wallets={wallets}>{children}</WalletProvider>
        </ConnectionProvider>
    )
}
